import React from 'react';
import { Megaphone, Info, Star, AlertTriangle, ArrowUpRight } from 'lucide-react';
import { linkCta } from '../lib/announcementLinks';

export const PRIORITIES = {
  info: { label: 'Info', Icon: Info, pill: 'bg-[#2B3A2C]/85 text-[#FBF3DF] ring-1 ring-[#FBF3DF]/25', dot: 'bg-[#FBF3DF]' },
  important: { label: 'Important', Icon: Star, pill: 'bg-[#FFF4D6] text-[#7A3E1D] ring-1 ring-[#7A3E1D]/25', dot: 'bg-[#D97706]' },
  urgent: { label: 'Urgent', Icon: AlertTriangle, pill: 'bg-[#B42318] text-white ring-2 ring-white/60 ticker-urgent', dot: 'bg-white' },
};

const TickerItem = ({ a, testid, onOpen }) => {
  const p = PRIORITIES[a.priority] || PRIORITIES.info;
  const { Icon } = p;
  const clickable = !!(a.link && onOpen);
  const Tag = clickable ? 'button' : 'span';
  return (
    <span className="inline-flex items-center px-3" data-testid={testid}>
      <Tag
        {...(clickable ? { type: 'button', onClick: () => onOpen(a.link), title: linkCta(a.link) } : {})}
        className={`inline-flex items-center gap-2 rounded-full pl-1.5 pr-4 py-1 shadow-sm ${p.pill} ${clickable ? 'cursor-pointer transition-transform hover:scale-[1.04] hover:shadow-md' : ''}`}
        data-testid={testid && clickable ? `${testid}-link` : undefined}
      >
        <span className="inline-flex items-center gap-1 rounded-full bg-black/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider">
          <Icon className="w-3 h-3" strokeWidth={2.4} /> {p.label}
        </span>
        <span className="text-sm font-semibold">{a.message}</span>
        {clickable && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-white/90 text-[#7A3E1D] px-2 py-0.5 text-[10px] font-bold">
            {linkCta(a.link)} <ArrowUpRight className="w-3 h-3" strokeWidth={2.6} />
          </span>
        )}
      </Tag>
      <span className="ml-6 w-1.5 h-1.5 rotate-45 bg-[#2B3A2C]/50" aria-hidden="true" />
    </span>
  );
};

export const NewsTicker = ({ items = [], action = null, className = '', onOpen = null }) => {
  if (!items.length && !action) return null;
  const baseLen = items.map((a) => a.message).join(' ').length || 1;
  const reps = Math.max(1, Math.ceil(160 / baseLen));
  const list = Array.from({ length: reps }, () => items).flat();
  const duration = Math.max(20, list.map((a) => a.message).join(' ').length * 0.2);
  const hasUrgent = items.some((a) => a.priority === 'urgent');

  return (
    <div
      className={`ticker-shell relative flex w-full min-w-0 items-stretch rounded-2xl overflow-hidden shadow-[0_8px_30px_-8px_rgba(184,92,56,0.55)] ring-1 ring-[#F6D58E]/60 ${className}`}
      style={{ backgroundImage: 'linear-gradient(100deg, #8C4A24 0%, #B85C38 22%, #D4A24C 50%, #E9C46A 62%, #B85C38 85%, #8C4A24 100%)' }}
      data-testid="news-ticker"
    >
      <span className="ticker-shine pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className={`relative z-10 flex items-center gap-2 px-3 sm:px-4 shrink-0 text-white ${hasUrgent ? 'bg-[#B42318]' : 'bg-[#2B3A2C]'}`} data-testid="ticker-flash-badge">
        <span className="relative flex w-2.5 h-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#F6D58E] opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-[#F6D58E]" />
        </span>
        <Megaphone className="w-4 h-4 ticker-wiggle" strokeWidth={2} />
        <span className="hidden sm:inline text-[11px] font-black uppercase tracking-[0.22em] ticker-blink">Flash info</span>
        <span className="absolute right-[-10px] top-0 h-full w-5 skew-x-[-14deg] bg-inherit" aria-hidden="true" />
      </div>
      <div className="ticker-viewport relative z-0 flex-1 min-w-0 overflow-hidden py-2 pl-4">
        {items.length ? (
          <div className="ticker-track whitespace-nowrap" style={{ animationDuration: `${duration}s` }}>
            {[0, 1].map((copy) => (
              <span key={copy} aria-hidden={copy === 1} className="inline-flex">
                {list.map((a, i) => (
                  <TickerItem key={`${copy}-${i}`} a={a} onOpen={onOpen} testid={copy === 0 && i < items.length ? `ticker-item-${i}` : undefined} />
                ))}
              </span>
            ))}
          </div>
        ) : (
          <p className="px-2 py-1 text-sm font-semibold text-white/90" data-testid="ticker-empty">Aucune actualité en cours — cliquez sur « Gérer » pour publier</p>
        )}
      </div>
      {action && <div className="relative z-10 flex items-center px-2 shrink-0 bg-[#2B3A2C]/90">{action}</div>}
    </div>
  );
};

export default NewsTicker;
